import {
  ApiDropdown,
  BadRequestResponseDTO,
} from 'src/services/api/data-contracts';

const validateEmail = (email: string): boolean => {
  return /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/.test(email);
};

const validatePassword = (password: string): boolean => {
  return password != '' && password != null;
};

const validatePhoneNumber = (phoneNumber: string): boolean => {
  return (
    (phoneNumber.startsWith('+') || phoneNumber.startsWith('00')) &&
    /[0-9/-]*/.test(phoneNumber)
  );
};

const isValidStringValue = (
  value: string | null | undefined,
  required: boolean,
): boolean => !required || (value?.trim()?.length ?? 0) > 0;

const isValidLookupValue = (
  value: number | null | undefined,
  required: boolean,
): boolean => !required || (value ?? 0) > 0;

const isValidObjectValue = (
  value: unknown | null | undefined,
  required: boolean,
): boolean => !required || (value != null && value != undefined);

const cardnameMatchPredicate = (text: string, cardName: string): boolean => {
  const str = text.toLowerCase();
  if (cardName == 'amex') {
    return str.includes(cardName) || str.includes('american express');
  }

  return str.includes(cardName);
};

const getCardType = (
  cardNumber: string,
  cardTypes: Array<ApiDropdown>,
): ApiDropdown | null => {
  let cardType: ApiDropdown | null = null;

  const name = getCardTypeName(cardNumber);

  if (name) {
    cardType = cardTypes.find((y) => cardnameMatchPredicate(y.Text, name));
  }

  return cardType;
};

const getCardTypeName = (cardNumber: string) => {
  const number = cardNumber;
  let re = new RegExp('^4');
  if (number.match(re) != null) {
    return 'visa';
  }

  re = new RegExp('^(34|37)');
  if (number.match(re) != null) {
    return 'amex';
  }

  re = new RegExp('^5[1-5]');
  if (number.match(re) != null) {
    return 'mastercard';
  }

  re = new RegExp('^6011');
  if (number.match(re) != null) {
    return 'discover';
  }

  re = new RegExp('^62');
  if (number.match(re) != null) {
    return 'unionpay';
  }

  re = new RegExp('^9792');
  if (number.match(re) != null) {
    return 'troy';
  }

  re = new RegExp('^3(?:0([0-5]|9)|[689]\\d?)\\d{0,11}');
  if (number.match(re) != null) {
    return 'dinersclub';
  }

  re = new RegExp('^35(2[89]|[3-8])');
  if (number.match(re) != null) {
    return 'jcb';
  }

  return ''; // default type
};

function getModelStateErrorsByKey(
  errorResponse: BadRequestResponseDTO,
): string {
  if (errorResponse != null && errorResponse.ModelState != null) {
    const errorMessages = Object.entries(errorResponse.ModelState)
      .flatMap(([key, errors]) => errors.map((error) => `${key}: ${error}`))
      .join(' ');

    return errorMessages;
  }
  return '';
}

function getModelStateErrors(errorResponse: BadRequestResponseDTO): string {
  if (errorResponse != null && errorResponse.ModelState != null) {
    const errorMessages = Object.entries(errorResponse.ModelState)
      .flatMap(([, errors]) => errors.map((error) => `${error}`))
      .join(' ');

    return errorMessages;
  }
  return '';
}

function getErrorMessage(errorResponse: BadRequestResponseDTO): string {
  if (errorResponse != null && errorResponse.ModelState != null) {
    const errorMessages = Object.entries(errorResponse.ModelState)
      .flatMap(([, errors]) => errors.map((error) => `${error}`))
      .join(' ');

    return errorMessages;
  }
  return errorResponse?.Message ?? null;
}

export default function useValidationUtils() {
  return {
    getCardType: getCardType,
    getCardTypeName: getCardTypeName,
    isValidStringValue: isValidStringValue,
    isValidLookupValue: isValidLookupValue,
    isValidObjectValue: isValidObjectValue,
    validateEmail: validateEmail,
    validatePassword: validatePassword,
    validatePhoneNumber: validatePhoneNumber,
    getModelStateErrors: getModelStateErrors,
    getModelStateErrorsByKey: getModelStateErrorsByKey,
    getErrorMessage: getErrorMessage,
  };
}
